import { toast } from 'react-toastify';

import { simulateContract, writeContract } from '@wagmi/core';

import { Address } from 'viem';

import { AsyncTransactionResult } from '@api';

import { wagmiConfig, WagmiConfigChain } from '@shared/common/providers/Web3Provider/wagmi';
import {
  BaseTokenDetails,
  namedWeb3Errors,
  SQR_TOKEN_CONTRACT,
  SQR_TOKEN_INFO,
} from '@shared/constants';
import { normalizeError } from '@shared/helpers/displayErrorToast';
import clearSavedConnectionDetails from '@shared/helpers/web3/clearSavedConnectionDetails';
import { numberToWei } from '@shared/helpers/web3/formatters/numberToWei';
import launchAsyncToast from '@shared/helpers/web3/launchAsyncToast';
import useMagicIdUserWalletInfo from '@shared/hooks/useMagicIdUserWalletInfo';
import useWeb3 from '@shared/hooks/web3/useWeb3';

const useIncreaseDepositAllowance = () => {
  const { connectWallet } = useWeb3();
  const { getUserWalletType } = useMagicIdUserWalletInfo();

  const increaseDepositAllowance = async (props: {
    amount: number | string;
    targetChain: WagmiConfigChain;
    spenderAddress: string;
    contractAddress?: Address;
    token?: BaseTokenDetails;
  }): AsyncTransactionResult => {
    const { token = SQR_TOKEN_INFO, amount, targetChain, spenderAddress, contractAddress } = props;
    const toastId = Date.now();

    try {
      // If allowance less of required amount call write-method approve(spender, amount),
      // Where spender - SQR Lockup contract address
      const { request } = await simulateContract(wagmiConfig, {
        ...SQR_TOKEN_CONTRACT,
        ...(contractAddress && { address: contractAddress }),
        functionName: 'approve',
        chainId: targetChain.id,
        gas: BigInt(300_000),
        args: [spenderAddress as `0x${string}`, numberToWei(amount, token.decimals).toBigInt()],
      });

      const { walletType, userWallet } = getUserWalletType();

      return (await Promise.race([
        writeContract(wagmiConfig, request),
        launchAsyncToast({
          walletType,
          address: userWallet.attributes.address,
          toastId,
          title: 'Increasing the deposit allowance...',
        }),
      ])) as Awaited<AsyncTransactionResult>;
    } catch (error) {
      const normalizedError = normalizeError(error);

      const reconnectAndRetry = async () => {
        const { walletType, userWallet } = getUserWalletType();

        await connectWallet({
          walletType,
          targetAddress: userWallet.attributes.address,
        });

        return increaseDepositAllowance(props);
      };

      if (normalizedError.message === namedWeb3Errors.CONNECTOR_NOT_FOUND) {
        clearSavedConnectionDetails();
        return await reconnectAndRetry();
      }

      if (normalizedError.message === namedWeb3Errors.RETRY) {
        return await increaseDepositAllowance(props);
      }

      throw error;
    } finally {
      toast.dismiss(toastId);
    }
  };

  return { increaseDepositAllowance };
};

export default useIncreaseDepositAllowance;
